import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useAuth } from '@/lib/contexts/AuthContext'
import {
  getAdminHealth,
  getAdminAdsAttribution,
  getAdminSignupAttribution,
  searchUsers,
  impersonateUser,
  actionDeleteAccountRequest,
  adminAddCustomSite,
  adminFixSubscription,
  adminRunSubscriptionReconciliation,
  adminChangeEmail,
  adminListContacts,
  adminCreateContact,
  adminUpdateContact,
  adminDeleteContact,
  adminAddContactComm,
  postAdminAuditEvent,
} from '@/lib/api/api'
import type {
  AdminHealthResponse,
  ImpersonateResponse,
  Contact,
  ContactsListResponse,
  AdsSignupAttributionResponse,
  SignupAttributionResponse,
} from '@/lib/api/admin-types'
import {
  logAdminEvent,
  reportAdminFailure,
  type AdminAuthor,
} from '@/lib/observability/admin'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Alert, AlertDescription } from '@/components/ui/alert'
import EnhancedMetricCard from '@/components/shared/EnhancedMetricCard'
import {
  PageShell,
  PageHeader,
  Panel,
  PanelHeader,
} from '@/components/shared/layout'
import UserHealthDrawer, {
  type AdminUserSummary,
} from '@/components/admin/UserHealthDrawer'
import SignupFunnelTab from '@/components/admin/SignupFunnelTab'
import AuditLogTab from '@/components/admin/AuditLogTab'
import { configureAdminAuditSink } from '@/lib/observability/adminAudit'

import { HugeiconsIcon } from '@hugeicons/react'
import {
  CancelCircleIcon,
  CheckmarkCircle01Icon,
  Loading01Icon,
  Search01Icon,
  UserCircleIcon,
  Add01Icon,
  Delete02Icon,
  Edit02Icon,
  FloppyDiskIcon,
  MessageMultiple01Icon,
} from '@hugeicons/core-free-icons'

type SearchUser = AdminUserSummary
type DeleteRequestAction = 'complete' | 'cancel'

const ADMIN_TAB_LABELS: Record<string, string> = {
  funnel: 'Signup Funnel',
  ads: 'Paid Ads',
  users: 'Users',
  leads: 'Leads',
  operations: 'Operations',
  audit: 'Audit log',
}

const fmtNumber = (n: number | null | undefined) => {
  if (n == null || Number.isNaN(n)) return '—'
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M'
  if (n >= 1_000) return (n / 1_000).toFixed(0) + 'K'
  return n.toLocaleString()
}

const fmtCurrency = (n: number | null | undefined) => {
  if (n == null || Number.isNaN(n)) return '—'
  if (n >= 1_000_000) return '$' + (n / 1_000_000).toFixed(2) + 'M'
  if (n >= 1_000) return '$' + (n / 1_000).toFixed(1) + 'K'
  return '$' + n.toLocaleString(undefined, { maximumFractionDigits: 0 })
}

const fmtPct = (n: number | null | undefined) => {
  if (n == null || Number.isNaN(n)) return '—'
  return `${n.toFixed(1)}%`
}


export default function Admin() {
  const { token, setToken, user } = useAuth()
  const navigate = useNavigate()

  // Basic-auth modal — password is the ADMIN_API_KEY
  const [showAuthModal, setShowAuthModal] = useState(true)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')

  // Page state
  const [loaded, setLoaded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('funnel')
  const [health, setHealth] = useState<AdminHealthResponse>({
    current: { tofu: { signups: 0, signups_with_site: 0, signups_no_site: 0, signups_with_trial: 0, signups_no_trial: 0, active_trials: 0, paid_subscribers: 0, aggregate_sites: 0, aggregate_events: 0 }, retention: { mrr: 0, arr: 0, arpa: 0, paid_customers: 0, customer_churn_rate: 0, mrr_churn_rate: 0, nrr: 0, grr: 0, quick_ratio: 0, cac: null, ltv: null, ltv_cac_ratio: null, mrr_movements: { new_business: 0, expansion: 0, contraction: 0, churn: 0, reactivation: 0, net: 0 }, pending_cancellations: [], at_risk_no_login: [], at_risk_no_script: [] }, cohort: [], growth_stats: [], view_stats: [] },
    previous: { tofu: { signups: 0, signups_with_site: 0, signups_no_site: 0, signups_with_trial: 0, signups_no_trial: 0, active_trials: 0, paid_subscribers: 0, aggregate_sites: 0, aggregate_events: 0 }, retention: { mrr: 0, arr: 0, arpa: 0, paid_customers: 0, customer_churn_rate: 0, mrr_churn_rate: 0, nrr: 0, grr: 0, quick_ratio: 0, cac: null, ltv: null, ltv_cac_ratio: null, mrr_movements: { new_business: 0, expansion: 0, contraction: 0, churn: 0, reactivation: 0, net: 0 }, pending_cancellations: [], at_risk_no_login: [], at_risk_no_script: [] }, cohort: [], growth_stats: [], view_stats: [] },
    delete_requests: [],
    contacts: [],
  })

  // Ads attribution state
  const [adsPreset, setAdsPreset] = useState('mtd')
  const [adsData, setAdsData] = useState<AdsSignupAttributionResponse | null>(null)
  const [adsLoading, setAdsLoading] = useState(false)
  const [expandedCampaign, setExpandedCampaign] = useState<string | null>(null)
  const [expandedUnmatchedAd, setExpandedUnmatchedAd] = useState<string | null>(null)
  const [expandedUnmatchedUtm, setExpandedUnmatchedUtm] = useState<string | null>(null)

  // Signup attribution (first-touch) state
  const [signupAttrData, setSignupAttrData] = useState<SignupAttributionResponse | null>(null)
  const [signupAttrLoading, setSignupAttrLoading] = useState(false)

  // Search / drawer
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchUser[]>([])
  const [searchError, setSearchError] = useState<string | null>(null)
  const [drawerUser, setDrawerUser] = useState<SearchUser | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Operations forms
  const [addSiteForm, setAddSiteForm] = useState({ email: '', domain: '', name: '', isFramer: false })
  const [addSiteLoading, setAddSiteLoading] = useState(false)
  const [addSiteMessage, setAddSiteMessage] = useState('')
  const [addSiteSuccess, setAddSiteSuccess] = useState(false)

  const [fixSubscriptionForm, setFixSubscriptionForm] = useState({ subscriptionId: '', userId: '' })
  const [fixSubscriptionLoading, setFixSubscriptionLoading] = useState(false)
  const [fixSubscriptionMessage, setFixSubscriptionMessage] = useState('')
  const [fixSubscriptionSuccess, setFixSubscriptionSuccess] = useState(false)

  const [changeEmailForm, setChangeEmailForm] = useState({ userId: '', newEmail: '' })
  const [changeEmailLoading, setChangeEmailLoading] = useState(false)
  const [changeEmailMessage, setChangeEmailMessage] = useState('')
  const [changeEmailSuccess, setChangeEmailSuccess] = useState(false)

  const [reconcileLoading, setReconcileLoading] = useState(false)
  const [reconcileMessage, setReconcileMessage] = useState('')
  const [reconcileSuccess, setReconcileSuccess] = useState(false)
  const [reconcileResults, setReconcileResults] = useState<any>(null)
  const [deleteRequestActions, setDeleteRequestActions] = useState<Record<string, DeleteRequestAction>>({})
  const deleteRequestActionsRef = useRef<Set<string>>(new Set())

  // CRM contacts state
  const [crmContacts, setCrmContacts] = useState<Contact[]>([])
  const [crmPagination, setCrmPagination] = useState<ContactsListResponse['pagination'] | null>(null)
  const [crmLoading, setCrmLoading] = useState(false)
  const [crmFilter, setCrmFilter] = useState<{ status: string; email: string; limit: number; offset: number }>({
    status: '', email: '', limit: 100, offset: 0,
  })
  // New contact form
  const [newContact, setNewContact] = useState({ name: '', email: '', company: '', source: '', notes: '', status: 'lead', user_id: '' })
  const [newContactLoading, setNewContactLoading] = useState(false)
  const [showNewContactForm, setShowNewContactForm] = useState(false)
  // Edit contact inline
  const [editingContact, setEditingContact] = useState<Contact | null>(null)
  const [editContactLoading, setEditContactLoading] = useState(false)
  // Delete confirm
  const [deletingContactId, setDeletingContactId] = useState<string | null>(null)
  // Comm log
  const [commContactId, setCommContactId] = useState<string | null>(null)
  const [commForm, setCommForm] = useState({ type: 'note', summary: '', date: '' })
  const [commLoading, setCommLoading] = useState(false)
  // Expanded contact row (to see comms)
  const [expandedContactId, setExpandedContactId] = useState<string | null>(null)

  /**
   * Frozen on first render: impersonating calls `setToken`, which re-decodes
   * `user` from the impersonated JWT. Reading the live value after that would
   * credit the admin's own actions to the account they just took over.
   */
  const adminIdentityRef = useRef<AdminAuthor>({})
  if (!adminIdentityRef.current.userId && user?.userId) {
    adminIdentityRef.current = { userId: user.userId, email: user.email, name: user.name }
  }

  const author = useMemo<AdminAuthor>(() => {
    const identity = adminIdentityRef.current
    return {
      userId: identity.userId ?? user?.userId ?? null,
      email: identity.email ?? user?.email ?? null,
      name: identity.name ?? user?.name ?? null,
      adminUsername: username || null,
    }
  }, [user, username])

  const authorRef = useRef(author)
  authorRef.current = author

  useEffect(() => {
    document.title = 'Admin · Platform Health — Humblytics'
    if (!token) {
      navigate('/sign-in')
    }
  }, [token, navigate])

  useEffect(() => {
    if (!token) {
      configureAdminAuditSink(null)
      return
    }
    configureAdminAuditSink((record) => {
      void postAdminAuditEvent(token, record)
    })
    return () => configureAdminAuditSink(null)
  }, [token])

  useEffect(() => {
    logAdminEvent('portal_opened', authorRef.current)
    return () => {
      logAdminEvent('portal_exited', authorRef.current)
    }
  }, [])

  useEffect(() => {
    if (!loaded || !password) return
    logAdminEvent('tab_viewed', authorRef.current, {
      admin_tab: ADMIN_TAB_LABELS[activeTab] ?? activeTab,
    })
  }, [activeTab, loaded, password])

  const loadHealth = useCallback(async () => {
    if (!token || !password) return
    setLoading(true)
    try {
      const res = await getAdminHealth(token)
      setHealth({
        current: res.current,
        previous: res.previous,
        delete_requests: res.delete_requests || [],
        contacts: res.contacts || [],
      })
      setLoaded(true)
    } catch (err) {
      reportAdminFailure('load_health', err, author)
      toast.error('Failed to load admin data — check credentials & backend')
    } finally {
      setLoading(false)
    }
  }, [token, password, author])

  const loadAds = useCallback(async () => {
    if (!token || !password) return
    setAdsLoading(true)
    try {
      const res = await getAdminAdsAttribution(token, adsPreset)
      setAdsData(res)
    } catch (err) {
      reportAdminFailure('load_ads_attribution', err, author, { preset: adsPreset })
      toast.error('Failed to load ads attribution')
    } finally {
      setAdsLoading(false)
    }
  }, [token, password, adsPreset, author])

  const loadSignupAttr = useCallback(async () => {
    if (!token || !password) return
    setSignupAttrLoading(true)
    try {
      const res = await getAdminSignupAttribution(token, adsPreset)
      setSignupAttrData(res)
    } catch (err) {
      reportAdminFailure('load_signup_attribution', err, author, { preset: adsPreset })
      toast.error('Failed to load signup attribution')
    } finally {
      setSignupAttrLoading(false)
    }
  }, [token, password, adsPreset, author])

  const handleAuthSubmit = () => {
    if (!username || !password) return
    setShowAuthModal(false)
    setLoaded(true)
    logAdminEvent('portal_login_submitted', author)
    loadHealth()
    loadAds()
    loadSignupAttr()
    loadCrmContacts()
  }

  const beginDeleteRequestAction = (requestId: string, action: DeleteRequestAction) => {
    if (deleteRequestActionsRef.current.has(requestId)) return false

    deleteRequestActionsRef.current.add(requestId)
    setDeleteRequestActions((current) => ({ ...current, [requestId]: action }))
    return true
  }

  const endDeleteRequestAction = (requestId: string) => {
    deleteRequestActionsRef.current.delete(requestId)
    setDeleteRequestActions((current) => {
      const next = { ...current }
      delete next[requestId]
      return next
    })
  }

  const removeDeleteRequest = (requestId: string) => {
    setHealth((current) => ({
      ...current,
      delete_requests: current.delete_requests.filter((request) => request.request_id !== requestId),
    }))
  }

  // ---------------- Search ----------------
  useEffect(() => {
    let cancelled = false
    const t = setTimeout(async () => {
      if (searchQuery.length < 3) {
        setSearchResults([])
        setSearchError(null)
        return
      }
      if (!token || !username || !password) return
      try {
        const data = await searchUsers(token, searchQuery, username, password)
        if (cancelled) return
        // Non-OK lookups throw; a 200 with a non-array body is still not a result list.
        if (!Array.isArray(data)) {
          setSearchResults([])
          setSearchError('Search failed. Try again or check credentials.')
          reportAdminFailure(
            'search_users',
            new Error((data as { error?: string })?.error || 'Unexpected search response'),
            author,
            { search_query: searchQuery }
          )
          return
        }
        setSearchError(null)
        setSearchResults(
          data.map((u: any) => ({
            ...u,
            stripeCustomerId: u.stripeCustomerId ?? null,
            stripeCustomerUrl: u.stripeCustomerUrl ?? null,
            subscription: u.subscription ?? null,
            websites: u.websites || [],
            workspaces: u.workspaces || [],
          }))
        )
        // The lookup itself is the audited member access, so the query is part of the trail.
        logAdminEvent('user_search_performed', author, {
          search_query: searchQuery,
          result_count: data.length,
        })
      } catch (err) {
        if (cancelled) return
        setSearchResults([])
        setSearchError('Search failed. Try again or check credentials.')
        reportAdminFailure('search_users', err, author, { search_query: searchQuery })
      }
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [searchQuery, token, username, password, author])

  // ---------------- Impersonate ----------------
  const handleImpersonate = useCallback(
    async (userId: string, email: string) => {
      if (!token || !username || !password) return
      try {
        const res: ImpersonateResponse = await impersonateUser(token, userId, username, password)
        const newToken = res?.authToken || res?.token || res?.accessToken
        if (newToken) {
          // Logged before the swap — `setToken` re-decodes the auth user.
          logAdminEvent('impersonation_session_started', author, {
            target_user_id: userId,
            target_email: email,
          })
          setToken(newToken)
          toast.success(`Impersonating ${email}`)
          setDrawerOpen(false)
          navigate('/dashboard')
        } else {
          // `impersonateUser` returns the parsed body without checking `response.ok`,
          // so a rejected call surfaces its reason here instead of throwing.
          reportAdminFailure(
            'impersonate_session',
            new Error(
              (res as { error?: string })?.error || 'Impersonation response missing auth token'
            ),
            author,
            { target_user_id: userId, target_email: email }
          )
          toast.error('Impersonation response missing auth token — check backend response shape')
        }
      } catch (err: any) {
        reportAdminFailure('impersonate_session', err, author, {
          target_user_id: userId,
          target_email: email,
        })
        toast.error(err?.message || 'Failed to impersonate')
      }
    },
    [token, username, password, setToken, navigate, author]
  )

  const handleCopyImpersonateLink = useCallback(
    async (userId: string, email: string) => {
      if (!token || !username || !password) return
      try {
        const res: ImpersonateResponse = await impersonateUser(token, userId, username, password)
        const newToken = res?.authToken || res?.token || res?.accessToken
        if (!newToken) {
          reportAdminFailure(
            'impersonate_link',
            new Error(
              (res as { error?: string })?.error || 'Impersonation response missing auth token'
            ),
            author,
            { target_user_id: userId, target_email: email }
          )
          toast.error('Impersonation response missing auth token')
          return
        }
        const url = new URL(window.location.origin + '/dashboard')
        url.searchParams.set('authToken', newToken)
        await navigator.clipboard.writeText(url.toString())
        logAdminEvent('impersonation_link_created', author, {
          target_user_id: userId,
          target_email: email,
          link_ttl: '8h',
        })
        toast.success(`Link copied for ${email} — valid 8h`)
      } catch (err: any) {
        reportAdminFailure('impersonate_link', err, author, {
          target_user_id: userId,
          target_email: email,
        })
        toast.error(err?.message || 'Failed to copy link')
      }
    },
    [token, username, password, author]
  )

  const openDrawer = (target: SearchUser) => {
    setDrawerUser(target)
    setDrawerOpen(true)
    logAdminEvent('user_health_viewed', author, {
      target_user_id: target.userId,
      target_email: target.email,
    })
  }

  // ---------------- Ops handlers ----------------
  const handleAddCustomSite = async () => {
    if (!addSiteForm.email || !addSiteForm.domain || !addSiteForm.name) {
      setAddSiteMessage('All fields are required')
      setAddSiteSuccess(false)
      return
    }
    if (!token || !password) return
    setAddSiteLoading(true)
    setAddSiteMessage('')
    try {
      const result = await adminAddCustomSite(
        addSiteForm.email,
        addSiteForm.domain,
        addSiteForm.name,
        token,
        addSiteForm.isFramer
      )
      if (result.duplicate) {
        setAddSiteMessage('Site already exists for this user')
        setAddSiteSuccess(false)
      } else if (result.unarchived) {
        setAddSiteMessage('Site unarchived successfully')
        setAddSiteSuccess(true)
        logAdminEvent('custom_site_added', author, {
          target_email: addSiteForm.email,
          domain: addSiteForm.domain,
          is_framer: addSiteForm.isFramer,
          outcome: 'unarchived',
        })
      } else if (result.tooManySites) {
        setAddSiteMessage('User has reached website limit for their plan')
        setAddSiteSuccess(false)
      } else {
        setAddSiteMessage(`Site added for ${result.targetUser?.email || addSiteForm.email}`)
        setAddSiteSuccess(true)
        logAdminEvent('custom_site_added', author, {
          target_email: result.targetUser?.email || addSiteForm.email,
          domain: addSiteForm.domain,
          is_framer: addSiteForm.isFramer,
          outcome: 'created',
        })
        setAddSiteForm({ email: '', domain: '', name: '', isFramer: false })
        toast.success('Site added')
      }
    } catch (err: any) {
      reportAdminFailure('add_custom_site', err, author, {
        target_email: addSiteForm.email,
        domain: addSiteForm.domain,
      })
      setAddSiteMessage(err.message || 'Failed to add site')
      setAddSiteSuccess(false)
    } finally {
      setAddSiteLoading(false)
    }
  }

  const handleFixSubscription = async () => {
    if (!fixSubscriptionForm.subscriptionId || !fixSubscriptionForm.userId) {
      setFixSubscriptionMessage('Both Subscription ID and User ID are required')
      setFixSubscriptionSuccess(false)
      return
    }
    if (!token) return
    setFixSubscriptionLoading(true)
    setFixSubscriptionMessage('')
    try {
      const result = await adminFixSubscription(
        fixSubscriptionForm.subscriptionId,
        fixSubscriptionForm.userId,
        token
      )
      if (result.success) {
        setFixSubscriptionMessage(
          `Subscription fixed — linked ${fixSubscriptionForm.subscriptionId} to ${fixSubscriptionForm.userId}`
        )
        setFixSubscriptionSuccess(true)
        logAdminEvent('subscription_fixed', author, {
          subscription_id: fixSubscriptionForm.subscriptionId,
          target_user_id: fixSubscriptionForm.userId,
        })
        setFixSubscriptionForm({ subscriptionId: '', userId: '' })
        toast.success('Subscription fixed')
      } else {
        setFixSubscriptionMessage('Failed to fix subscription')
        setFixSubscriptionSuccess(false)
      }
    } catch (err: any) {
      reportAdminFailure('fix_subscription', err, author, {
        subscription_id: fixSubscriptionForm.subscriptionId,
        target_user_id: fixSubscriptionForm.userId,
      })
      setFixSubscriptionMessage(err.message || 'Failed to fix subscription')
      setFixSubscriptionSuccess(false)
    } finally {
      setFixSubscriptionLoading(false)
    }
  }

  const handleChangeEmail = async () => {
    if (!changeEmailForm.userId || !changeEmailForm.newEmail) {
      setChangeEmailMessage('Both User ID and new email are required')
      setChangeEmailSuccess(false)
      return
    }
    if (!token || !username || !password) return
    setChangeEmailLoading(true)
    setChangeEmailMessage('')
    try {
      const result = await adminChangeEmail(
        changeEmailForm.userId,
        changeEmailForm.newEmail,
        token,
        username,
        password
      )
      if (result.success) {
        setChangeEmailMessage(
          `Email updated: ${result.oldEmail} → ${result.newEmail}`
        )
        setChangeEmailSuccess(true)
        logAdminEvent('user_email_changed', author, {
          target_user_id: changeEmailForm.userId,
          old_email: result.oldEmail ?? null,
          new_email: result.newEmail ?? changeEmailForm.newEmail,
        })
        setChangeEmailForm({ userId: '', newEmail: '' })
        toast.success('Email updated')
      } else {
        setChangeEmailMessage('Failed to change email')
        setChangeEmailSuccess(false)
      }
    } catch (err: any) {
      reportAdminFailure('change_email', err, author, {
        target_user_id: changeEmailForm.userId,
        new_email: changeEmailForm.newEmail,
      })
      setChangeEmailMessage(err.message || 'Failed to change email')
      setChangeEmailSuccess(false)
    } finally {
      setChangeEmailLoading(false)
    }
  }

  const handleRunReconciliation = async () => {
    if (!token) return
    setReconcileLoading(true)
    setReconcileMessage('')
    setReconcileResults(null)
    try {
      const result = await adminRunSubscriptionReconciliation(token)
      const results = result.results ?? result
      setReconcileMessage('Reconciliation complete')
      setReconcileSuccess(true)
      setReconcileResults(results)
      logAdminEvent('reconciliation_run', author, {
        result_count: Array.isArray(results) ? results.length : null,
      })
      toast.success('Subscription reconciliation complete')
    } catch (err: any) {
      reportAdminFailure('run_reconciliation', err, author)
      setReconcileMessage(err.message || 'Failed to run reconciliation')
      setReconcileSuccess(false)
    } finally {
      setReconcileLoading(false)
    }
  }

  const handleAccountDeletion = async (requestId: string, requestUserId: string) => {
    if (!token || !username || !password) return
    if (!beginDeleteRequestAction(requestId, 'complete')) return

    try {
      await actionDeleteAccountRequest(token, 'complete', requestId, requestUserId, username, password)
      logAdminEvent('account_deletion_completed', author, {
        request_id: requestId,
        target_user_id: requestUserId,
      })
      toast.success('Account deletion completed')
      removeDeleteRequest(requestId)
      void loadHealth()
    } catch (err) {
      reportAdminFailure('complete_account_deletion', err, author, {
        request_id: requestId,
        target_user_id: requestUserId,
      })
      toast.error('Failed to complete deletion')
    } finally {
      endDeleteRequestAction(requestId)
    }
  }

  const handleCancelRequest = async (requestId: string, requestUserId: string) => {
    if (!token || !username || !password) return
    if (!beginDeleteRequestAction(requestId, 'cancel')) return

    try {
      await actionDeleteAccountRequest(token, 'cancel', requestId, requestUserId, username, password)
      logAdminEvent('account_deletion_cancelled', author, {
        request_id: requestId,
        target_user_id: requestUserId,
      })
      toast.success('Request cancelled')
      removeDeleteRequest(requestId)
      void loadHealth()
    } catch (err) {
      reportAdminFailure('cancel_account_deletion', err, author, {
        request_id: requestId,
        target_user_id: requestUserId,
      })
      toast.error('Failed to cancel request')
    } finally {
      endDeleteRequestAction(requestId)
    }
  }

  // ---- CRM handlers ----
  const loadCrmContacts = useCallback(async (filter = crmFilter) => {
    if (!token || !password) return
    setCrmLoading(true)
    try {
      const res = await adminListContacts(token, {
        status: filter.status || undefined,
        email: filter.email || undefined,
        limit: filter.limit,
        offset: filter.offset,
      })
      setCrmContacts(res.contacts)
      setCrmPagination(res.pagination)
    } catch (err) {
      reportAdminFailure('load_contacts', err, author)
      toast.error('Failed to load contacts')
    } finally {
      setCrmLoading(false)
    }
  }, [token, password, crmFilter, author])

  const handleCreateContact = async () => {
    if (!token || !password) return
    setNewContactLoading(true)
    try {
      const contact = await adminCreateContact(token, {
        name: newContact.name || undefined,
        email: newContact.email || undefined,
        company: newContact.company || undefined,
        source: newContact.source || undefined,
        notes: newContact.notes || undefined,
        status: newContact.status || 'lead',
        user_id: newContact.user_id || undefined,
      })
      setCrmContacts((prev) => [contact, ...prev])
      logAdminEvent('contact_created', author, {
        contact_id: contact.id,
        contact_email: contact.email ?? null,
        contact_status: contact.status ?? null,
      })
      setNewContact({ name: '', email: '', company: '', source: '', notes: '', status: 'lead', user_id: '' })
      setShowNewContactForm(false)
      toast.success('Contact created')
    } catch (err) {
      reportAdminFailure('create_contact', err, author, { contact_email: newContact.email || null })
      toast.error('Failed to create contact')
    } finally {
      setNewContactLoading(false)
    }
  }

  const handleUpdateContact = async () => {
    if (!token || !password || !editingContact) return
    setEditContactLoading(true)
    try {
      const updated = await adminUpdateContact(token, editingContact.id, {
        name: editingContact.name ?? undefined,
        email: editingContact.email ?? undefined,
        company: editingContact.company ?? undefined,
        status: editingContact.status,
        source: editingContact.source ?? undefined,
        notes: editingContact.notes ?? undefined,
        user_id: editingContact.user_id ?? undefined,
      })
      setCrmContacts((prev) => prev.map((c) => c.id === updated.id ? updated : c))
      logAdminEvent('contact_updated', author, {
        contact_id: updated.id,
        contact_email: updated.email ?? null,
        contact_status: updated.status ?? null,
      })
      setEditingContact(null)
      toast.success('Contact updated')
    } catch (err) {
      reportAdminFailure('update_contact', err, author, { contact_id: editingContact.id })
      toast.error('Failed to update contact')
    } finally {
      setEditContactLoading(false)
    }
  }

  const handleDeleteContact = async (id: string) => {
    if (!token || !password) return
    try {
      await adminDeleteContact(token, id)
      setCrmContacts((prev) => prev.filter((c) => c.id !== id))
      logAdminEvent('contact_deleted', author, { contact_id: id })
      setDeletingContactId(null)
      toast.success('Contact deleted')
    } catch (err) {
      reportAdminFailure('delete_contact', err, author, { contact_id: id })
      toast.error('Failed to delete contact')
    }
  }

  const handleAddComm = async () => {
    if (!token || !password || !commContactId || !commForm.type || !commForm.summary) return
    setCommLoading(true)
    try {
      const updated = await adminAddContactComm(token, commContactId, {
        type: commForm.type,
        summary: commForm.summary,
        date: commForm.date || undefined,
      })
      setCrmContacts((prev) => prev.map((c) => c.id === updated.id ? updated : c))
      logAdminEvent('contact_comm_logged', author, {
        contact_id: commContactId,
        comm_type: commForm.type,
      })
      setCommForm({ type: 'note', summary: '', date: '' })
      setCommContactId(null)
      toast.success('Comm logged')
    } catch (err) {
      reportAdminFailure('add_contact_comm', err, author, {
        contact_id: commContactId,
        comm_type: commForm.type,
      })
      toast.error('Failed to log comm')
    } finally {
      setCommLoading(false)
    }
  }

  const cur = health.current

  if (!token) return null

  return (
    <>
      {/* Basic-auth credentials modal */}
      <AlertDialog open={showAuthModal} onOpenChange={setShowAuthModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Admin Authentication</AlertDialogTitle>
            <AlertDialogDescription>
              Enter your admin basic-auth credentials. These are used alongside your existing auth token.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col gap-3 mt-2">
            <Input
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAuthSubmit()}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => navigate('/')}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleAuthSubmit} disabled={!username || !password}>
              Authorize
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {loaded && (
        <main className="min-h-screen bg-background">
          <PageShell>
            <PageHeader
              title="Admin · Platform Health"
              description="Live platform metrics and operator tooling. Source: admin/health."
              actions={
                loading ? (
                  <HugeiconsIcon icon={Loading01Icon} size={16} className="animate-spin text-muted-foreground" />
                ) : undefined
              }
            />


            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="funnel">Signup Funnel</TabsTrigger>
                <TabsTrigger value="ads">Paid Ads</TabsTrigger>
                <TabsTrigger value="users">Users</TabsTrigger>
                <TabsTrigger value="leads">Leads</TabsTrigger>
                <TabsTrigger value="operations">Operations</TabsTrigger>
                <TabsTrigger value="audit">Audit log</TabsTrigger>
              </TabsList>

              {/* ============ Signup Funnel ============ */}
              <TabsContent value="funnel">
                <SignupFunnelTab token={token} authorized={loaded && !!password} author={author} />
              </TabsContent>

              <TabsContent value="audit">
                <AuditLogTab token={token} authorized={loaded && !!password} author={author} />
              </TabsContent>

              {/* ============ Paid Ads Attribution ============ */}
              <TabsContent value="ads">
            <Panel className="mb-4">
              <PanelHeader
                title="Paid Ads Attribution"
                description="Ad spend → signup funnel, matched via UTM sessions on humblytics.com"
                action={
                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      value={adsPreset}
                      onChange={(e) => setAdsPreset(e.target.value)}
                      className="text-xs border border-border/30 rounded px-2 py-1 bg-background text-foreground"
                    >
                      <option value="24h">Last 24h</option>
                      <option value="7d">Last 7d</option>
                      <option value="wtd">Week to date</option>
                      <option value="mtd">Month to date</option>
                      <option value="qtd">Quarter to date</option>
                      <option value="ytd">Year to date</option>
                    </select>
                    <Button size="sm" variant="outline" onClick={() => { loadAds(); loadSignupAttr() }} disabled={adsLoading || signupAttrLoading}>
                      {(adsLoading || signupAttrLoading)
                        ? <HugeiconsIcon icon={Loading01Icon} size={12} className="animate-spin" />
                        : 'Load'}
                    </Button>
                  </div>
                }
              />

              {adsData && (
                <>
                  {/* Totals */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-2 mt-4">
                    <EnhancedMetricCard label="Total Spend" value={fmtCurrency(adsData.totals?.spend)} />
                    <EnhancedMetricCard label="Impressions" value={fmtNumber(adsData.totals?.impressions)} />
                    <EnhancedMetricCard label="Clicks" value={fmtNumber(adsData.totals?.clicks)} />
                    <EnhancedMetricCard label="Sessions" value={fmtNumber(adsData.totals?.sessions)} />
                    <EnhancedMetricCard label="Signups" value={fmtNumber(adsData.totals?.signups_converted)} />
                    <EnhancedMetricCard label="Paid" value={fmtNumber(adsData.totals?.paid_conversions)} />
                    <EnhancedMetricCard label="Cost / Signup" value={fmtCurrency(adsData.totals?.cost_per_signup)} />
                  </div>

                  {/* Campaigns table */}
                  {(adsData.campaigns?.length ?? 0) > 0 && (
                    <div className="mt-6">
                      <div className="text-xs font-medium text-muted-foreground mb-2">
                        Campaigns ({adsData.campaigns.length})
                      </div>
                      <div className="rounded-md border border-border/30 overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-muted/30 hover:bg-muted/30">
                              <TableHead className="text-xs font-semibold">Campaign</TableHead>
                              <TableHead className="text-xs font-semibold">Platform</TableHead>
                              <TableHead className="text-xs font-semibold text-right">Spend</TableHead>
                              <TableHead className="text-xs font-semibold text-right">Clicks</TableHead>
                              <TableHead className="text-xs font-semibold text-right">Sessions</TableHead>
                              <TableHead className="text-xs font-semibold text-right">Signups</TableHead>
                              <TableHead className="text-xs font-semibold text-right">Paid</TableHead>
                              <TableHead className="text-xs font-semibold text-right">Cost/Signup</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(adsData.campaigns ?? []).map((campaign) => (
                              <>
                                <TableRow key={campaign.campaign} className="border-b-0">
                                  <TableCell>
                                    <div className="text-sm font-medium">{campaign.campaign}</div>
                                    <div className="text-xs text-muted-foreground">{campaign.top_landing_page || '—'}</div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex gap-1 flex-wrap">
                                      {(campaign.platforms ?? []).map((p) => (
                                        <Badge key={p} variant="secondary" className="text-[10px]">{p}</Badge>
                                      ))}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-sm text-right tabular-nums">{fmtCurrency(campaign.spend)}</TableCell>
                                  <TableCell className="text-sm text-right tabular-nums">{fmtNumber(campaign.clicks)}</TableCell>
                                  <TableCell className="text-sm text-right tabular-nums">{fmtNumber(campaign.sessions)}</TableCell>
                                  <TableCell className="text-sm text-right tabular-nums">{fmtNumber(campaign.funnel?.signups_converted)}</TableCell>
                                  <TableCell className="text-sm text-right tabular-nums">{fmtNumber(campaign.funnel?.paid_conversions)}</TableCell>
                                  <TableCell className="text-sm text-right tabular-nums">{fmtCurrency(campaign.cost_per_signup)}</TableCell>
                                </TableRow>
                                <TableRow key={`${campaign.campaign}-detail`} className="bg-muted/10 border-b border-border/30">
                                  <TableCell colSpan={8} className="py-3 px-6 pb-4">
                                    {/* Stats row */}
                                    <div className="flex flex-wrap gap-6 mb-3">
                                      {([
                                        ['Signups Converted', fmtNumber(campaign.funnel?.signups_converted)],
                                        ['Signups Completed', fmtNumber(campaign.funnel?.signups_completed)],
                                        ['Sites Added', fmtNumber(campaign.funnel?.sites_added)],
                                        ['Experiments', fmtNumber(campaign.funnel?.experiments_published)],
                                        ['Active Subs', fmtNumber(campaign.funnel?.active_subscriptions)],
                                        ['Paid', fmtNumber(campaign.funnel?.paid_conversions)],
                                        ['Click→Session', campaign.click_to_session_rate != null ? fmtPct(campaign.click_to_session_rate * 100) : '—'],
                                        ['Session→Signup', campaign.session_to_signup_rate != null ? fmtPct(campaign.session_to_signup_rate * 100) : '—'],
                                        ['Ad Conversions', fmtNumber(campaign.ad_conversions)],
                                        ['Cost/Trial', fmtCurrency(campaign.cost_per_trial)],
                                        ['Cost/Paid', fmtCurrency(campaign.cost_per_paid)],
                                      ] as [string, string][]).map(([label, val]) => (
                                        <div key={label} className="flex flex-col gap-0.5">
                                          <span className="text-xs text-muted-foreground">{label}</span>
                                          <span className="text-sm font-semibold tabular-nums">{val}</span>
                                        </div>
                                      ))}
                                    </div>
                                    {/* Converted users */}
                                    {(campaign.converted_users?.length ?? 0) > 0 && (
                                      <div>
                                        <div className="text-xs font-medium text-muted-foreground mb-1.5">
                                          Converted Users ({campaign.converted_users.length})
                                        </div>
                                        <div className="space-y-1">
                                          {campaign.converted_users.map((u, i) => (
                                            <div key={i} className="flex items-center gap-3 text-xs">
                                              <span className="font-medium w-32 shrink-0 truncate">{u.name || '—'}</span>
                                              <span className="text-muted-foreground font-mono flex-1 truncate">{u.email || '—'}</span>
                                              {u.is_fuzzy_match && (
                                                <span className="text-[10px] font-semibold px-1 py-0.5 rounded bg-warning/15 text-warning dark:text-warning shrink-0">fuzzy</span>
                                              )}
                                              {u.subscription_status && (
                                                <Badge variant={u.subscription_status === 'ACTIVE' ? 'default' : 'secondary'} className="text-[10px] shrink-0">
                                                  {u.subscription_status}
                                                </Badge>
                                              )}
                                              <span className="text-muted-foreground tabular-nums shrink-0">{new Date(u.date_created).toLocaleDateString()}</span>
                                              {u.possible_duplicate_of && (
                                                <span className="text-[10px] text-warning shrink-0">⚠ dup of {u.possible_duplicate_of.email || u.possible_duplicate_of.user_id}</span>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </TableCell>
                                </TableRow>
                              </>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}

                  {/* Unmatched — full width, always expanded */}
                  {((adsData.unmatched_ad_campaigns?.length ?? 0) > 0 || (adsData.unmatched_utm_campaigns?.length ?? 0) > 0) && (
                    <div className="space-y-6 mt-6">
                      {(adsData.unmatched_ad_campaigns?.length ?? 0) > 0 && (
                        <div>
                          <div className="text-xs font-medium text-muted-foreground mb-2">
                            Unmatched Ad Campaigns ({adsData.unmatched_ad_campaigns.length})
                          </div>
                          <div className="rounded-md border border-border/30 overflow-hidden">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-muted/30 hover:bg-muted/30">
                                  <TableHead className="text-xs font-semibold">Campaign</TableHead>
                                  <TableHead className="text-xs font-semibold">Platform</TableHead>
                                  <TableHead className="text-xs font-semibold text-right">Spend</TableHead>
                                  <TableHead className="text-xs font-semibold text-right">Impressions</TableHead>
                                  <TableHead className="text-xs font-semibold">Target URLs</TableHead>
                                  <TableHead className="text-xs font-semibold">Note</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {adsData.unmatched_ad_campaigns.map((c, i) => (
                                  <TableRow key={i}>
                                    <TableCell className="text-sm font-medium">{c.campaign}</TableCell>
                                    <TableCell>
                                      <div className="flex gap-1">
                                        {(c.platforms ?? []).map((p) => (
                                          <Badge key={p} variant="secondary" className="text-[10px]">{p}</Badge>
                                        ))}
                                      </div>
                                    </TableCell>
                                    <TableCell className="text-sm text-right tabular-nums">{fmtCurrency(c.spend)}</TableCell>
                                    <TableCell className="text-sm text-right tabular-nums">{fmtNumber(c.impressions)}</TableCell>
                                    <TableCell className="text-xs text-muted-foreground font-mono">
                                      {c.target_urls?.length > 0 ? c.target_urls.join(', ') : '—'}
                                    </TableCell>
                                    <TableCell className="text-xs text-muted-foreground italic">
                                      {c.note || 'No UTM sessions matched.'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      )}
                      {(adsData.unmatched_utm_campaigns?.length ?? 0) > 0 && (
                        <div>
                          <div className="text-xs font-medium text-muted-foreground mb-2">
                            Unmatched UTM Campaigns ({adsData.unmatched_utm_campaigns.length})
                          </div>
                          <div className="rounded-md border border-border/30 overflow-hidden">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-muted/30 hover:bg-muted/30">
                                  <TableHead className="text-xs font-semibold">Campaign</TableHead>
                                  <TableHead className="text-xs font-semibold">Source / Medium</TableHead>
                                  <TableHead className="text-xs font-semibold">Landing Page</TableHead>
                                  <TableHead className="text-xs font-semibold text-right">Sessions</TableHead>
                                  <TableHead className="text-xs font-semibold text-right">Signups</TableHead>
                                  <TableHead className="text-xs font-semibold text-right">Paid</TableHead>
                                  <TableHead className="text-xs font-semibold">Converted Users</TableHead>
                                  <TableHead className="text-xs font-semibold">Note</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {adsData.unmatched_utm_campaigns.map((c, i) => (
                                  <TableRow key={i}>
                                    <TableCell className="text-sm font-medium max-w-[180px] truncate" title={c.utm_campaign || ''}>{c.utm_campaign || '—'}</TableCell>
                                    <TableCell className="text-xs text-muted-foreground">{[c.utm_source, c.utm_medium].filter(Boolean).join(' / ') || '—'}</TableCell>
                                    <TableCell className="text-xs text-muted-foreground font-mono">{c.top_landing_page || '—'}</TableCell>
                                    <TableCell className="text-sm text-right tabular-nums">{fmtNumber(c.sessions)}</TableCell>
                                    <TableCell className="text-sm text-right tabular-nums">{fmtNumber(c.signups_converted)}</TableCell>
                                    <TableCell className="text-sm text-right tabular-nums">{fmtNumber(c.paid_conversions)}</TableCell>
                                    <TableCell className="text-xs">
                                      {!c.converted_users?.length ? (
                                        <span className="text-muted-foreground">None</span>
                                      ) : (
                                        <div className="space-y-1.5">
                                          {c.converted_users.map((u, j) => (
                                            <div key={j} className="space-y-0.5">
                                              <div className="flex items-center gap-1.5">
                                                <span className="font-medium">{u.name || '—'}</span>
                                                <span className="font-mono text-muted-foreground">{u.email || u.user_id}</span>
                                                {u.is_fuzzy_match && (
                                                  <span className="text-[10px] font-semibold px-1 py-0.5 rounded bg-warning/15 text-warning dark:text-warning">fuzzy</span>
                                                )}
                                                {u.subscription_status && (
                                                  <Badge variant={u.subscription_status === 'ACTIVE' ? 'default' : 'secondary'} className="text-[10px]">
                                                    {u.subscription_status}
                                                  </Badge>
                                                )}
                                                <span className="text-muted-foreground tabular-nums">{new Date(u.date_created).toLocaleDateString()}</span>
                                              </div>
                                              {u.possible_duplicate_of && (
                                                <div className="text-[10px] text-warning">⚠ dup of {u.possible_duplicate_of.email || u.possible_duplicate_of.user_id}</div>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-xs text-muted-foreground italic">{c.note || '—'}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {!adsData && !adsLoading && (
                <p className="text-sm text-muted-foreground text-center py-8">Select a date range and click Load to fetch ads attribution.</p>
              )}

              {/* ---- Signup Attribution (first-touch) ---- */}
              {signupAttrLoading && (
                <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
                  <HugeiconsIcon icon={Loading01Icon} size={14} className="animate-spin" />
                  Loading signup attribution…
                </div>
              )}
              {signupAttrData && (
                <div className="mt-6">
                  <div className="text-xs font-medium text-muted-foreground mb-2">
                    Signup Attribution — First Touch ({signupAttrData.date_range.startDate} → {signupAttrData.date_range.endDate})
                  </div>
                  <div className="rounded-md border border-border/30 overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/30 hover:bg-muted/30">
                          <TableHead className="text-xs font-semibold">Source</TableHead>
                          <TableHead className="text-xs font-semibold">Medium</TableHead>
                          <TableHead className="text-xs font-semibold">Campaign</TableHead>
                          <TableHead className="text-xs font-semibold">Group</TableHead>
                          <TableHead className="text-xs font-semibold text-right">Signups</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {signupAttrData.data.map((row, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-sm font-medium">{row.source}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{row.medium}</TableCell>
                            <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate" title={row.campaign}>{row.campaign}</TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="text-[10px]">{row.referrer_group}</Badge>
                            </TableCell>
                            <TableCell className="text-sm text-right tabular-nums font-semibold">{fmtNumber(row.signups)}</TableCell>
                          </TableRow>
                        ))}
                        {signupAttrData.data.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-4">No signup attribution data for this period.</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </Panel>
              </TabsContent>

              {/* ============ Users ============ */}
              <TabsContent value="users">
                <Panel>
                  <PanelHeader
                    title="User Search"
                    description="Search by user ID, name, email, or domain — click a row for details"
                  />
                  <div className="relative mt-3">
                    <HugeiconsIcon
                      icon={Search01Icon}
                      size={14}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                    />
                    <Input
                      type="text"
                      placeholder="Search…"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>

                  {searchResults.length > 0 && (
                    <div className="mt-4 rounded-md border border-border/30 overflow-hidden">
                      <TooltipProvider>
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/30 hover:bg-muted/30">
                            <TableHead className="text-xs font-semibold">User ID</TableHead>
                            <TableHead className="text-xs font-semibold">Name</TableHead>
                            <TableHead className="text-xs font-semibold">Email</TableHead>
                            <TableHead className="text-xs font-semibold">Subscription</TableHead>
                            <TableHead className="text-xs font-semibold">Sites</TableHead>
                            <TableHead className="text-xs font-semibold">Workspaces</TableHead>
                            <TableHead className="text-xs font-semibold text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {searchResults.map((user) => (
                            <TableRow key={user.userId} className="hover:bg-muted/20">
                              <TableCell className="font-mono text-xs text-muted-foreground">
                                {user.userId}
                              </TableCell>
                              <TableCell className="text-sm font-medium">{user.name}</TableCell>
                              <TableCell className="text-sm text-muted-foreground">{user.email}</TableCell>
                              <TableCell>
                                {user.subscription ? (
                                  <div className="flex items-center gap-1.5">
                                    {user.subscription.trial ? (
                                      <Badge variant="secondary" className="text-[10px]">Trial</Badge>
                                    ) : user.subscription.status === 'ACTIVE' ? (
                                      <Badge variant="default" className="text-[10px]">Active</Badge>
                                    ) : (
                                      <Badge variant="outline" className="text-[10px]">{user.subscription.status}</Badge>
                                    )}
                                    <a href={user.subscription.stripeSubscriptionUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] text-primary underline underline-offset-2">sub ↗</a>
                                    {user.stripeCustomerUrl && (
                                      <a href={user.stripeCustomerUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] text-primary underline underline-offset-2">cus ↗</a>
                                    )}
                                  </div>
                                ) : user.stripeCustomerUrl ? (
                                  <a href={user.stripeCustomerUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] text-primary underline underline-offset-2">cus ↗</a>
                                ) : (
                                  <span className="text-xs text-muted-foreground">—</span>
                                )}
                              </TableCell>
                              <TableCell>
                                {user.websites.length === 0 ? (
                                  <Badge variant="secondary" className="text-[10px]">0</Badge>
                                ) : (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Badge variant="secondary" className="text-[10px] cursor-default">
                                        {user.websites.length}
                                      </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="max-w-xs">
                                      <div className="space-y-0.5">
                                        {user.websites.map((site, i) => (
                                          <div key={i} className="font-mono">{site}</div>
                                        ))}
                                      </div>
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                              </TableCell>
                              <TableCell>
                                <Badge variant="secondary" className="text-[10px]">
                                  {user.workspaces.length}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-1.5">
                                  <Button size="sm" variant="outline" onClick={() => openDrawer(user)}>
                                    <HugeiconsIcon icon={UserCircleIcon} size={12} className="mr-1" />
                                    Open
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={() => handleImpersonate(user.userId, user.email)}
                                  >
                                    Impersonate
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleCopyImpersonateLink(user.userId, user.email)}
                                    title="Copy impersonation link to clipboard"
                                  >
                                    Copy link
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      </TooltipProvider>
                    </div>
                  )}

                  {searchError && searchQuery.length >= 3 && (
                    <p className="text-center text-sm text-destructive py-8">{searchError}</p>
                  )}

                  {!searchError && searchResults.length === 0 && searchQuery.length >= 3 && (
                    <p className="text-center text-sm text-muted-foreground py-8">No users found.</p>
                  )}

                  {searchQuery.length > 0 && searchQuery.length < 3 && (
                    <p className="text-center text-xs text-muted-foreground py-8">
                      Enter at least 3 characters to search.
                    </p>
                  )}
                </Panel>
              </TabsContent>

              {/* ============ Leads ============ */}
              <TabsContent value="leads">
                <Panel>
                  <PanelHeader
                    title="CRM Contacts"
                    description={crmPagination ? `${crmPagination.total} total · showing ${crmPagination.returned}` : 'Leads, trials, and churned users'}
                    action={
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => loadCrmContacts()} disabled={crmLoading}>
                          {crmLoading
                            ? <HugeiconsIcon icon={Loading01Icon} size={12} className="animate-spin" />
                            : 'Refresh'}
                        </Button>
                        <Button size="sm" onClick={() => setShowNewContactForm((v) => !v)}>
                          <HugeiconsIcon icon={Add01Icon} size={12} className="mr-1" />
                          Add Contact
                        </Button>
                      </div>
                    }
                  />

                  {/* Filters */}
                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    <Input
                      placeholder="Filter by email (auto-creates if not found)"
                      value={crmFilter.email}
                      onChange={(e) => setCrmFilter((f) => ({ ...f, email: e.target.value, offset: 0 }))}
                      className="text-xs h-8 w-64"
                    />
                    <select
                      value={crmFilter.status}
                      onChange={(e) => setCrmFilter((f) => ({ ...f, status: e.target.value, offset: 0 }))}
                      className="text-xs border border-border/30 rounded px-2 py-1 bg-background text-foreground h-8"
                    >
                      <option value="">All statuses</option>
                      <option value="lead">lead</option>
                      <option value="trial">trial</option>
                      <option value="active">active</option>
                      <option value="paused">paused</option>
                      <option value="churned">churned</option>
                      <option value="not_activated">not_activated</option>
                    </select>
                    <Button size="sm" variant="outline" className="h-8" onClick={() => loadCrmContacts(crmFilter)}>
                      Search
                    </Button>
                  </div>

                  {/* New contact form */}
                  {showNewContactForm && (
                    <div className="mt-4 p-4 rounded-md border border-border/30 bg-muted/20">
                      <div className="text-xs font-medium text-muted-foreground mb-3">New contact</div>
                      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                        {(['name', 'email', 'company', 'source', 'user_id'] as const).map((field) => (
                          <div key={field} className="space-y-1">
                            <Label className="text-[10px] font-semibold uppercase text-muted-foreground">{field.replace('_', ' ')}</Label>
                            <Input
                              value={newContact[field]}
                              onChange={(e) => setNewContact((p) => ({ ...p, [field]: e.target.value }))}
                              className="text-xs h-8"
                              placeholder={field === 'user_id' ? 'UUID (optional)' : undefined}
                            />
                          </div>
                        ))}
                        <div className="space-y-1">
                          <Label className="text-[10px] font-semibold uppercase text-muted-foreground">Status</Label>
                          <select
                            value={newContact.status}
                            onChange={(e) => setNewContact((p) => ({ ...p, status: e.target.value }))}
                            className="w-full text-xs border border-border/30 rounded px-2 py-1 bg-background text-foreground h-8"
                          >
                            <option value="lead">lead</option>
                            <option value="trial">trial</option>
                            <option value="active">active</option>
                            <option value="paused">paused</option>
                            <option value="churned">churned</option>
                            <option value="not_activated">not_activated</option>
                          </select>
                        </div>
                      </div>
                      <div className="mt-3 space-y-1">
                        <Label className="text-[10px] font-semibold uppercase text-muted-foreground">Notes</Label>
                        <Input
                          value={newContact.notes}
                          onChange={(e) => setNewContact((p) => ({ ...p, notes: e.target.value }))}
                          className="text-xs h-8"
                        />
                      </div>
                      <div className="flex gap-2 justify-end mt-3">
                        <Button size="sm" variant="outline" onClick={() => setShowNewContactForm(false)}>Cancel</Button>
                        <Button size="sm" onClick={handleCreateContact} disabled={newContactLoading}>
                          {newContactLoading ? <HugeiconsIcon icon={Loading01Icon} size={12} className="animate-spin mr-1" /> : null}
                          Create
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Contacts table */}
                  {crmContacts.length === 0 && !crmLoading ? (
                    <p className="text-sm text-muted-foreground py-8 text-center">No contacts. Use Search to load or Add Contact to create one.</p>
                  ) : (
                    <div className="mt-3 rounded-md border border-border/30 overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/30 hover:bg-muted/30">
                            <TableHead className="text-xs font-semibold">Name / Email</TableHead>
                            <TableHead className="text-xs font-semibold">Company</TableHead>
                            <TableHead className="text-xs font-semibold">Status</TableHead>
                            <TableHead className="text-xs font-semibold">Source</TableHead>
                            <TableHead className="text-xs font-semibold">Notes</TableHead>
                            <TableHead className="text-xs font-semibold text-right">Created</TableHead>
                            <TableHead className="text-xs font-semibold text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {crmContacts.map((c: Contact) => {
                            const isExpanded = expandedContactId === c.id
                            const isEditing = editingContact?.id === c.id
                            const isDeleteConfirm = deletingContactId === c.id
                            const isCommOpen = commContactId === c.id
                            return (
                              <>
                                <TableRow key={c.id} className="hover:bg-muted/20">
                                  <TableCell>
                                    {isEditing ? (
                                      <div className="space-y-1">
                                        <Input
                                          value={editingContact.name ?? ''}
                                          onChange={(e) => setEditingContact((p) => p ? { ...p, name: e.target.value } : p)}
                                          className="text-xs h-7 w-40"
                                          placeholder="Name"
                                        />
                                        <Input
                                          value={editingContact.email ?? ''}
                                          onChange={(e) => setEditingContact((p) => p ? { ...p, email: e.target.value } : p)}
                                          className="text-xs h-7 w-40"
                                          placeholder="Email"
                                        />
                                      </div>
                                    ) : (
                                      <>
                                        <div className="text-sm font-medium">{c.name || '—'}</div>
                                        <div className="text-xs text-muted-foreground font-mono">{c.email || c.user_email || '—'}</div>
                                      </>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {isEditing ? (
                                      <Input
                                        value={editingContact.company ?? ''}
                                        onChange={(e) => setEditingContact((p) => p ? { ...p, company: e.target.value } : p)}
                                        className="text-xs h-7 w-28"
                                      />
                                    ) : (
                                      <span className="text-sm">{c.company || '—'}</span>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {isEditing ? (
                                      <select
                                        value={editingContact.status}
                                        onChange={(e) => setEditingContact((p) => p ? { ...p, status: e.target.value } : p)}
                                        className="text-xs border border-border/30 rounded px-1 py-0.5 bg-background text-foreground"
                                      >
                                        <option value="lead">lead</option>
                                        <option value="trial">trial</option>
                                        <option value="active">active</option>
                                        <option value="paused">paused</option>
                                        <option value="churned">churned</option>
                                        <option value="not_activated">not_activated</option>
                                      </select>
                                    ) : (
                                      <Badge
                                        variant={c.status === 'active' ? 'default' : c.status === 'trial' ? 'secondary' : 'outline'}
                                        className="text-[10px]"
                                      >
                                        {c.status}
                                      </Badge>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {isEditing ? (
                                      <Input
                                        value={editingContact.source ?? ''}
                                        onChange={(e) => setEditingContact((p) => p ? { ...p, source: e.target.value } : p)}
                                        className="text-xs h-7 w-24"
                                      />
                                    ) : (
                                      <span className="text-xs text-muted-foreground">{c.source || '—'}</span>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {isEditing ? (
                                      <Input
                                        value={editingContact.notes ?? ''}
                                        onChange={(e) => setEditingContact((p) => p ? { ...p, notes: e.target.value } : p)}
                                        className="text-xs h-7 w-36"
                                      />
                                    ) : (
                                      <span className="text-xs text-muted-foreground max-w-xs truncate block" title={c.notes || ''}>
                                        {c.notes || '—'}
                                      </span>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-xs text-muted-foreground text-right tabular-nums whitespace-nowrap">
                                    {new Date(c.created_at).toLocaleDateString()}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {isDeleteConfirm ? (
                                      <div className="flex items-center justify-end gap-1">
                                        <span className="text-xs text-muted-foreground mr-1">Delete?</span>
                                        <Button size="sm" variant="destructive" className="h-6 px-2 text-[10px]" onClick={() => handleDeleteContact(c.id)}>Yes</Button>
                                        <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => setDeletingContactId(null)}>No</Button>
                                      </div>
                                    ) : isEditing ? (
                                      <div className="flex items-center justify-end gap-1">
                                        <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => setEditingContact(null)}>Cancel</Button>
                                        <Button size="sm" className="h-6 px-2 text-[10px]" onClick={handleUpdateContact} disabled={editContactLoading}>
                                          {editContactLoading ? <HugeiconsIcon icon={Loading01Icon} size={10} className="animate-spin" /> : <HugeiconsIcon icon={FloppyDiskIcon} size={10} />}
                                        </Button>
                                      </div>
                                    ) : (
                                      <div className="flex items-center justify-end gap-1">
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-6 w-6 p-0"
                                          title="Log comm"
                                          onClick={() => { setCommContactId(isCommOpen ? null : c.id); setExpandedContactId(c.id) }}
                                        >
                                          <HugeiconsIcon icon={MessageMultiple01Icon} size={12} />
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-6 w-6 p-0"
                                          title="Edit"
                                          onClick={() => setEditingContact({ ...c })}
                                        >
                                          <HugeiconsIcon icon={Edit02Icon} size={12} />
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                                          title="Delete"
                                          onClick={() => setDeletingContactId(c.id)}
                                        >
                                          <HugeiconsIcon icon={Delete02Icon} size={12} />
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-6 px-1.5 text-[10px]"
                                          onClick={() => setExpandedContactId(isExpanded ? null : c.id)}
                                        >
                                          {isExpanded ? '▲' : `▼ ${c.comms?.length ?? 0}`}
                                        </Button>
                                      </div>
                                    )}
                                  </TableCell>
                                </TableRow>

                                {/* Expanded: comms history + add comm form */}
                                {isExpanded && (
                                  <TableRow key={`${c.id}-expanded`} className="bg-muted/10">
                                    <TableCell colSpan={7} className="py-3 px-6">
                                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                                        {/* Comms history */}
                                        <div>
                                          <div className="text-xs font-medium text-muted-foreground mb-2">
                                            Comms History ({c.comms?.length ?? 0})
                                          </div>
                                          {!c.comms?.length ? (
                                            <p className="text-xs text-muted-foreground">No comms logged yet.</p>
                                          ) : (
                                            <div className="space-y-2 max-h-48 overflow-y-auto">
                                              {[...c.comms].reverse().map((comm, i) => (
                                                <div key={i} className="text-xs border-l-2 border-border/30 pl-3 space-y-0.5">
                                                  <div className="flex items-center gap-2">
                                                    <Badge variant="secondary" className="text-[10px]">{comm.type}</Badge>
                                                    <span className="text-muted-foreground tabular-nums">
                                                      {new Date(comm.date).toLocaleString()}
                                                    </span>
                                                  </div>
                                                  <div className="text-foreground">{comm.summary}</div>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>

                                        {/* Log new comm */}
                                        {isCommOpen && (
                                          <div>
                                            <div className="text-xs font-medium text-muted-foreground mb-2">Log communication</div>
                                            <div className="space-y-2">
                                              <div className="flex gap-2">
                                                <select
                                                  value={commForm.type}
                                                  onChange={(e) => setCommForm((p) => ({ ...p, type: e.target.value }))}
                                                  className="text-xs border border-border/30 rounded px-2 py-1 bg-background text-foreground h-8"
                                                >
                                                  <option value="note">note</option>
                                                  <option value="email">email</option>
                                                  <option value="call">call</option>
                                                  <option value="slack">slack</option>
                                                </select>
                                                <Input
                                                  type="datetime-local"
                                                  value={commForm.date}
                                                  onChange={(e) => setCommForm((p) => ({ ...p, date: e.target.value }))}
                                                  className="text-xs h-8 flex-1"
                                                />
                                              </div>
                                              <Input
                                                placeholder="Summary (required)"
                                                value={commForm.summary}
                                                onChange={(e) => setCommForm((p) => ({ ...p, summary: e.target.value }))}
                                                className="text-xs h-8"
                                              />
                                              <div className="flex gap-2 justify-end">
                                                <Button size="sm" variant="outline" className="h-7" onClick={() => setCommContactId(null)}>Cancel</Button>
                                                <Button size="sm" className="h-7" onClick={handleAddComm} disabled={commLoading || !commForm.summary}>
                                                  {commLoading ? <HugeiconsIcon icon={Loading01Icon} size={10} className="animate-spin mr-1" /> : null}
                                                  Log
                                                </Button>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </>
                            )
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                  {/* Pagination */}
                  {crmPagination && crmPagination.total > crmPagination.limit && (
                    <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
                      <span>{crmPagination.offset + 1}–{Math.min(crmPagination.offset + crmPagination.returned, crmPagination.total)} of {crmPagination.total}</span>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          disabled={crmPagination.offset === 0}
                          onClick={() => {
                            const f = { ...crmFilter, offset: Math.max(0, crmFilter.offset - crmFilter.limit) }
                            setCrmFilter(f)
                            loadCrmContacts(f)
                          }}
                        >
                          ← Prev
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          disabled={crmPagination.offset + crmPagination.returned >= crmPagination.total}
                          onClick={() => {
                            const f = { ...crmFilter, offset: crmFilter.offset + crmFilter.limit }
                            setCrmFilter(f)
                            loadCrmContacts(f)
                          }}
                        >
                          Next →
                        </Button>
                      </div>
                    </div>
                  )}
                </Panel>
              </TabsContent>

              {/* ============ Operations ============ */}
              <TabsContent value="operations">
              <Tabs defaultValue="sites" className="w-full">
                <TabsList className="grid w-full grid-cols-5 mb-4">
                  <TabsTrigger value="sites">Add Site</TabsTrigger>
                  <TabsTrigger value="subscription">Fix Subscription</TabsTrigger>
                  <TabsTrigger value="reconcile">Reconcile</TabsTrigger>
                  <TabsTrigger value="email">Change Email</TabsTrigger>
                  <TabsTrigger value="deletion">Deletion</TabsTrigger>
                </TabsList>

              <TabsContent value="sites">
                <Panel>
                  <PanelHeader title="Add Custom Site" description="Add a website to a user's account" />
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="user-email" className="text-xs font-semibold">User Email</Label>
                      <Input
                        id="user-email"
                        type="email"
                        placeholder="user@example.com"
                        value={addSiteForm.email}
                        onChange={(e) => setAddSiteForm((p) => ({ ...p, email: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="site-domain" className="text-xs font-semibold">Domain</Label>
                      <Input
                        id="site-domain"
                        type="text"
                        placeholder="example.com"
                        value={addSiteForm.domain}
                        onChange={(e) => setAddSiteForm((p) => ({ ...p, domain: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="site-name" className="text-xs font-semibold">Site Name</Label>
                      <Input
                        id="site-name"
                        type="text"
                        placeholder="My Website"
                        value={addSiteForm.name}
                        onChange={(e) => setAddSiteForm((p) => ({ ...p, name: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold">Options</Label>
                      <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-border/30">
                        <Checkbox
                          id="framer"
                          checked={addSiteForm.isFramer}
                          onCheckedChange={(c) =>
                            setAddSiteForm((p) => ({ ...p, isFramer: !!c }))
                          }
                        />
                        <Label htmlFor="framer" className="text-xs cursor-pointer">Framer site</Label>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end mt-4">
                    <Button onClick={handleAddCustomSite} disabled={addSiteLoading}>
                      {addSiteLoading ? (
                        <>
                          <HugeiconsIcon icon={Loading01Icon} size={14} className="animate-spin mr-2" />
                          Adding…
                        </>
                      ) : (
                        'Add Site'
                      )}
                    </Button>
                  </div>
                  {addSiteMessage && (
                    <Alert
                      className={`mt-4 ${
                        addSiteSuccess
                          ? 'border-success/40 text-success dark:text-success'
                          : 'border-danger/40 text-danger'
                      }`}
                    >
                      <HugeiconsIcon
                        icon={addSiteSuccess ? CheckmarkCircle01Icon : CancelCircleIcon}
                        size={14}
                      />
                      <AlertDescription>{addSiteMessage}</AlertDescription>
                    </Alert>
                  )}
                </Panel>
              </TabsContent>

              <TabsContent value="subscription">
                <Panel>
                  <PanelHeader
                    title="Fix Stripe Subscription"
                    description="Manually link a Stripe subscription to a user account"
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="sub-id" className="text-xs font-semibold">Subscription ID</Label>
                      <Input
                        id="sub-id"
                        type="text"
                        placeholder="sub_xxxxxxxxxxxxx"
                        value={fixSubscriptionForm.subscriptionId}
                        onChange={(e) =>
                          setFixSubscriptionForm((p) => ({ ...p, subscriptionId: e.target.value }))
                        }
                      />
                      <p className="text-[10px] text-muted-foreground">Starts with 'sub_'</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="fix-user-id" className="text-xs font-semibold">User ID</Label>
                      <Input
                        id="fix-user-id"
                        type="text"
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        value={fixSubscriptionForm.userId}
                        onChange={(e) =>
                          setFixSubscriptionForm((p) => ({ ...p, userId: e.target.value }))
                        }
                      />
                      <p className="text-[10px] text-muted-foreground">UUID of the target user</p>
                    </div>
                  </div>
                  <div className="flex justify-end mt-4">
                    <Button onClick={handleFixSubscription} disabled={fixSubscriptionLoading}>
                      {fixSubscriptionLoading ? (
                        <>
                          <HugeiconsIcon icon={Loading01Icon} size={14} className="animate-spin mr-2" />
                          Fixing…
                        </>
                      ) : (
                        'Fix Subscription'
                      )}
                    </Button>
                  </div>
                  {fixSubscriptionMessage && (
                    <Alert
                      className={`mt-4 ${
                        fixSubscriptionSuccess
                          ? 'border-success/40 text-success dark:text-success'
                          : 'border-danger/40 text-danger'
                      }`}
                    >
                      <HugeiconsIcon
                        icon={fixSubscriptionSuccess ? CheckmarkCircle01Icon : CancelCircleIcon}
                        size={14}
                      />
                      <AlertDescription>{fixSubscriptionMessage}</AlertDescription>
                    </Alert>
                  )}
                </Panel>
              </TabsContent>

              <TabsContent value="reconcile">
                <Panel>
                  <PanelHeader
                    title="Subscription Reconciliation"
                    description="Re-sync all Stripe subscriptions against the database"
                  />
                  <p className="text-sm text-muted-foreground mt-4">
                    Runs the full reconciliation job — corrects mismatched or missing subscription records by
                    re-querying Stripe. Safe to run at any time; no destructive changes.
                  </p>
                  <div className="flex justify-end mt-4">
                    <Button onClick={handleRunReconciliation} disabled={reconcileLoading}>
                      {reconcileLoading ? (
                        <>
                          <HugeiconsIcon icon={Loading01Icon} size={14} className="animate-spin mr-2" />
                          Running…
                        </>
                      ) : (
                        'Run Reconciliation'
                      )}
                    </Button>
                  </div>
                  {reconcileMessage && (
                    <Alert
                      className={`mt-4 ${
                        reconcileSuccess
                          ? 'border-success/40 text-success dark:text-success'
                          : 'border-danger/40 text-danger'
                      }`}
                    >
                      <HugeiconsIcon
                        icon={reconcileSuccess ? CheckmarkCircle01Icon : CancelCircleIcon}
                        size={14}
                      />
                      <AlertDescription>{reconcileMessage}</AlertDescription>
                    </Alert>
                  )}
                  {reconcileResults && (
                    <div className="mt-4 rounded-md border border-border/30 bg-muted/30 p-3">
                      <p className="text-xs font-medium text-muted-foreground mb-2">Results</p>
                      <pre className="text-xs overflow-auto whitespace-pre-wrap break-all">
                        {JSON.stringify(reconcileResults, null, 2)}
                      </pre>
                    </div>
                  )}
                </Panel>
              </TabsContent>

              <TabsContent value="email">
                <Panel>
                  <PanelHeader
                    title="Change User Email"
                    description="Admin-only — bypasses verification"
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="ce-user-id" className="text-xs font-semibold">User ID</Label>
                      <Input
                        id="ce-user-id"
                        type="text"
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        value={changeEmailForm.userId}
                        onChange={(e) =>
                          setChangeEmailForm((p) => ({ ...p, userId: e.target.value }))
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new-email" className="text-xs font-semibold">New Email</Label>
                      <Input
                        id="new-email"
                        type="email"
                        placeholder="newemail@example.com"
                        value={changeEmailForm.newEmail}
                        onChange={(e) =>
                          setChangeEmailForm((p) => ({ ...p, newEmail: e.target.value }))
                        }
                      />
                    </div>
                  </div>
                  <div className="flex justify-end mt-4">
                    <Button onClick={handleChangeEmail} disabled={changeEmailLoading}>
                      {changeEmailLoading ? (
                        <>
                          <HugeiconsIcon icon={Loading01Icon} size={14} className="animate-spin mr-2" />
                          Changing…
                        </>
                      ) : (
                        'Change Email'
                      )}
                    </Button>
                  </div>
                  {changeEmailMessage && (
                    <Alert
                      className={`mt-4 ${
                        changeEmailSuccess
                          ? 'border-success/40 text-success dark:text-success'
                          : 'border-danger/40 text-danger'
                      }`}
                    >
                      <HugeiconsIcon
                        icon={changeEmailSuccess ? CheckmarkCircle01Icon : CancelCircleIcon}
                        size={14}
                      />
                      <AlertDescription>{changeEmailMessage}</AlertDescription>
                    </Alert>
                  )}
                </Panel>
              </TabsContent>

              <TabsContent value="deletion">
                <Panel>
                  <PanelHeader
                    title="Account Deletion Requests"
                    description={`${health.delete_requests.length} request(s)`}
                  />
                  {health.delete_requests.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-8 text-center">
                      No pending deletion requests.
                    </p>
                  ) : (
                    <div className="mt-3 rounded-md border border-border/30 overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/30 hover:bg-muted/30">
                            <TableHead className="text-xs font-semibold">Email</TableHead>
                            <TableHead className="text-xs font-semibold">User ID</TableHead>
                            <TableHead className="text-xs font-semibold">Reason</TableHead>
                            <TableHead className="text-xs font-semibold">Requested</TableHead>
                            <TableHead className="text-xs font-semibold">Status</TableHead>
                            <TableHead className="text-xs font-semibold text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {health.delete_requests
                            .slice()
                            .sort((a, b) => new Date(b.createdat).getTime() - new Date(a.createdat).getTime())
                            .map((req) => {
                              const activeAction = deleteRequestActions[req.request_id]

                              return (
                                <TableRow key={req.request_id}>
                                  <TableCell className="text-sm font-medium">{req.email}</TableCell>
                                  <TableCell className="font-mono text-xs text-muted-foreground">
                                    {req.user_id}
                                  </TableCell>
                                  <TableCell className="text-sm max-w-xs truncate" title={req.reason}>
                                    {req.reason}
                                  </TableCell>
                                  <TableCell className="text-xs text-muted-foreground">
                                    {new Date(req.createdat).toLocaleString()}
                                  </TableCell>
                                  <TableCell>
                                    {req.completed ? (
                                      <Badge variant="secondary" className="text-[10px]">Completed</Badge>
                                    ) : req.cancelled ? (
                                      <Badge variant="outline" className="text-[10px]">Cancelled</Badge>
                                    ) : (
                                      <Badge className="text-[10px]">Pending</Badge>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {!req.completed && !req.cancelled ? (
                                      <div className="flex justify-end gap-1.5">
                                        <Button
                                          size="sm"
                                          variant="destructive"
                                          disabled={Boolean(activeAction)}
                                          onClick={() => handleAccountDeletion(req.request_id, req.user_id)}
                                        >
                                          {activeAction === 'complete' ? (
                                            <>
                                              <HugeiconsIcon icon={Loading01Icon} size={12} className="animate-spin" />
                                              Deleting
                                            </>
                                          ) : (
                                            'Delete'
                                          )}
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          disabled={Boolean(activeAction)}
                                          onClick={() => handleCancelRequest(req.request_id, req.user_id)}
                                        >
                                          {activeAction === 'cancel' ? (
                                            <>
                                              <HugeiconsIcon icon={Loading01Icon} size={12} className="animate-spin" />
                                              Cancelling
                                            </>
                                          ) : (
                                            'Cancel'
                                          )}
                                        </Button>
                                      </div>
                                    ) : (
                                      <span className="text-xs text-muted-foreground">—</span>
                                    )}
                                  </TableCell>
                                </TableRow>
                              )
                            })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </Panel>
              </TabsContent>
              </Tabs>
              </TabsContent>

            </Tabs>
          </PageShell>

          <UserHealthDrawer
            user={drawerUser}
            open={drawerOpen}
            onOpenChange={setDrawerOpen}
            onImpersonate={handleImpersonate}
            onCopyImpersonateLink={handleCopyImpersonateLink}
          />
        </main>
      )}
    </>
  )
}
